import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Users, Eye, Calendar, Globe, Clock, Smartphone, Monitor, Tablet, FileText, Activity } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, AreaChart, Area } from "recharts";
import api from "@/lib/api";

function StatCard({ icon: Icon, label, value, change, hint, testId, accent }) {
  const positive = change != null && change > 0;
  const negative = change != null && change < 0;
  return (
    <div
      className="bg-white border border-[var(--border)] rounded-2xl p-5 flex flex-col gap-2"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className={`w-9 h-9 grid place-items-center rounded-xl ${accent || "bg-[var(--surface-alt)] text-[var(--primary)]"}`}>
          <Icon size={16} />
        </div>
        {change != null && (
          <span
            className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-1 ${
              positive
                ? "bg-green-50 text-green-700"
                : negative
                ? "bg-red-50 text-red-700"
                : "bg-stone-100 text-stone-600"
            }`}
          >
            {positive && <TrendingUp size={11} />}
            {negative && <TrendingDown size={11} />}
            {change > 0 ? "+" : ""}
            {change}%
          </span>
        )}
      </div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--text-muted)] mt-1">
        {label}
      </div>
      <div className="font-serif-display text-3xl sm:text-4xl font-black tabular-nums leading-none">{value}</div>
      {hint && <div className="text-xs text-[var(--text-muted)]">{hint}</div>}
    </div>
  );
}

function fmtDateLabel(d) {
  const [, m, day] = d.split("-");
  const months = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  return `${parseInt(day, 10)} ${months[parseInt(m, 10) - 1] || ""}`;
}

function fmtNum(n) {
  return (n ?? 0).toLocaleString("sv-SE");
}

const DEVICE_META = {
  desktop: { Icon: Monitor, label: "Dator", color: "bg-[var(--primary)]" },
  mobile: { Icon: Smartphone, label: "Mobil", color: "bg-[var(--text)]" },
  tablet: { Icon: Tablet, label: "Surfplatta", color: "bg-amber-500" },
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/analytics");
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // live refresh every 30s
    return () => clearInterval(id);
  }, []);

  if (loading) return <div className="py-12 text-center text-[var(--text-muted)]">Laddar statistik…</div>;
  if (!data) return <div className="py-12 text-center text-[var(--text-muted)]">Kunde inte ladda statistik.</div>;

  const t = data.totals;
  const c = data.change;
  const daily = (data.daily || []).map((d) => ({ ...d, label: fmtDateLabel(d.date) }));
  const last7 = daily.slice(-7);
  const refs = data.top_referrers || [];
  const pages = data.top_pages || [];
  const hourly = data.hourly || [];
  const devices = data.devices || {};
  const deviceTotal = Object.values(devices).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-serif-display text-3xl sm:text-4xl font-black">Statistik</h1>
          <p className="text-[var(--text-muted)] mt-1">
            Anonyma sidvisningar · Stockholm-tid · uppdateras live var 30:e sekund
            {t.days_online > 0 && <> · {t.days_online} dagar online</>}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 text-xs text-[var(--text-muted)] bg-white border border-[var(--border)] rounded-full px-3 py-1.5">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Live
        </div>
      </div>

      {/* Top stat row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Activity}
          label="Senaste timmen"
          value={fmtNum(t.this_hour)}
          hint="just nu på sidan"
          testId="stat-hour"
          accent="bg-green-50 text-green-600"
        />
        <StatCard
          icon={Eye}
          label="Idag"
          value={fmtNum(t.today)}
          change={c.vs_yesterday_pct}
          hint={`${fmtNum(t.unique_today)} unika · igår ${fmtNum(t.yesterday)}`}
          testId="stat-today"
        />
        <StatCard
          icon={TrendingUp}
          label="Senaste 7 dagarna"
          value={fmtNum(t.last_7d)}
          change={c.vs_prev_7d_pct}
          hint={`${fmtNum(t.unique_7d)} unika besökare`}
          testId="stat-7d"
        />
        <StatCard
          icon={Calendar}
          label="Senaste 30 dagarna"
          value={fmtNum(t.last_30d)}
          change={c.vs_prev_30d_pct}
          hint={`${fmtNum(t.unique_30d)} unika · snitt ${t.avg_per_day}/dag`}
          testId="stat-30d"
        />
      </div>

      {/* All-time row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <StatCard
          icon={FileText}
          label="Totalt sedan start"
          value={fmtNum(t.all_time)}
          hint={`${fmtNum(t.unique_all_time)} unika besökare totalt`}
          testId="stat-alltime"
          accent="bg-[var(--text)] text-white"
        />
        <StatCard
          icon={Users}
          label="Snitt / dag"
          value={t.avg_per_day}
          hint={`Beräknat på ${t.days_online} dagar`}
          testId="stat-avg"
        />
        <StatCard
          icon={Clock}
          label="Igår"
          value={fmtNum(t.yesterday)}
          hint="hela gårdagen"
          testId="stat-yesterday"
        />
      </div>

      {/* Daily area chart */}
      <div className="bg-white border border-[var(--border)] rounded-2xl p-6">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-serif-display text-xl font-bold">Senaste 30 dagarna</h3>
          <span className="text-xs text-[var(--text-muted)]">Sidvisningar per dag · Stockholm-tid</span>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="areaVisits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E04F39" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#E04F39" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#F4D8B3" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#57534E" }}
                tickLine={false}
                axisLine={false}
                interval={Math.max(0, Math.floor(daily.length / 8) - 1)}
              />
              <YAxis tick={{ fontSize: 11, fill: "#57534E" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4", fontSize: 13 }}
                labelStyle={{ fontWeight: 600 }}
                formatter={(v, k) => [v, k === "visits" ? "Besök" : "Unika"]}
              />
              <Area type="monotone" dataKey="visits" stroke="#E04F39" strokeWidth={2.5} fill="url(#areaVisits)" />
              <Line type="monotone" dataKey="unique" stroke="#1C1917" strokeWidth={2} strokeDasharray="4 4" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 flex gap-5 text-xs text-[var(--text-muted)]">
          <span className="inline-flex items-center gap-2">
            <span className="w-3 h-0.5 bg-[var(--primary)]" /> Besök
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="w-3 h-0.5 border-t-2 border-dashed border-[var(--text)]" /> Unika
          </span>
        </div>
      </div>

      {/* Hourly + Devices */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[var(--border)] rounded-2xl p-6">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-serif-display text-xl font-bold">Idag per timme</h3>
            <span className="text-xs text-[var(--text-muted)]">När besökarna kommer</span>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourly} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#F4D8B3" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#57534E" }} tickLine={false} axisLine={false} interval={1} />
                <YAxis tick={{ fontSize: 11, fill: "#57534E" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4", fontSize: 13 }}
                  formatter={(v) => [v, "Besök"]}
                  labelFormatter={(l) => `Kl ${l}:00`}
                />
                <Bar dataKey="visits" fill="#FFB663" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[var(--border)] rounded-2xl p-6">
          <h3 className="font-serif-display text-xl font-bold mb-4">Enheter (30d)</h3>
          {Object.keys(devices).length === 0 ? (
            <div className="text-sm text-[var(--text-muted)] py-6 text-center">Ingen data ännu</div>
          ) : (
            <ul className="space-y-3">
              {["mobile", "desktop", "tablet"].map((k) => {
                const n = devices[k] || 0;
                const pct = Math.round((n / deviceTotal) * 100);
                const meta = DEVICE_META[k];
                if (!meta) return null;
                const { Icon, label, color } = meta;
                return (
                  <li key={k} data-testid={`device-${k}`}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="inline-flex items-center gap-2 font-medium">
                        <Icon size={14} className="text-[var(--text-muted)]" />
                        {label}
                      </span>
                      <span className="text-[var(--text-muted)] tabular-nums">
                        {fmtNum(n)} <span className="text-stone-400">·</span> {pct}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-[var(--surface-alt)] overflow-hidden">
                      <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Last 7 days bar + Top referrers + Top pages */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[var(--border)] rounded-2xl p-6">
          <h3 className="font-serif-display text-xl font-bold mb-4">Senaste 7 dagarna</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={last7} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#F4D8B3" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#57534E" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#57534E" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4", fontSize: 13 }}
                  formatter={(v, k) => [v, k === "visits" ? "Besök" : "Unika"]}
                />
                <Bar dataKey="visits" fill="#E04F39" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[var(--border)] rounded-2xl p-6 space-y-6">
          <div>
            <h3 className="font-serif-display text-lg font-bold mb-3">Toppkällor</h3>
            {refs.length === 0 ? (
              <div className="text-xs text-[var(--text-muted)] py-2">Inga externa hänvisare</div>
            ) : (
              <ul className="space-y-2">
                {refs.map((r) => (
                  <li key={r.referrer} className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate" title={r.referrer}>
                      {(() => {
                        try { return new URL(r.referrer).hostname; } catch { return r.referrer; }
                      })()}
                    </span>
                    <span className="font-semibold tabular-nums">{r.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-[var(--border)] pt-5">
            <h3 className="font-serif-display text-lg font-bold mb-3">Toppsidor</h3>
            {pages.length === 0 ? (
              <div className="text-xs text-[var(--text-muted)] py-2">Ingen data</div>
            ) : (
              <ul className="space-y-2">
                {pages.map((p) => (
                  <li key={p.path} className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate font-mono text-xs" title={p.path}>{p.path}</span>
                    <span className="font-semibold tabular-nums">{p.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
